import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Lock, AlertCircle, CheckCircle, Eye, EyeOff, ArrowLeft
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function SetPasswordPage() {
  const { user, token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fromCheckout = searchParams.get('from') === 'checkout';

  useEffect(() => {
    // Must be logged in to set a password
    if (!user && !token) {
      navigate('/login');
    }
  }, [user, token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/set-password`,
        { password },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setSuccess(true);
      // Refresh /me so the "claim your account" banner disappears.
      // Failure here is non-fatal (user is already logged in with the new
      // password) — we just log it for diagnostics rather than surface it.
      try {
        await checkAuth();
      } catch (refreshErr) {
        console.warn('[set-password] /me refresh after set-password failed:', refreshErr?.message || refreshErr);
      }
      // Send the user to their dashboard
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-slate-900">MISYOU</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12" data-testid="set-password-page">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-14 h-14 bg-emerald-50 rounded-2xl mx-auto mb-3 flex items-center justify-center">
              <Lock className="w-7 h-7 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">
              {fromCheckout ? 'Claim your MISYOU account' : 'Set your password'}
            </CardTitle>
            <CardDescription>
              {fromCheckout
                ? 'Your payment is confirmed. Choose a password so you can sign back in any time.'
                : 'Choose a new password for your account.'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {success ? (
              <Alert className="bg-emerald-50 border-emerald-200" data-testid="set-password-success">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800">
                  Password set successfully. Taking you to your dashboard…
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {user?.email && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700">
                    Signed in as <strong>{user.email}</strong>
                  </div>
                )}

                {error && (
                  <Alert variant="destructive" data-testid="set-password-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-12 h-12"
                      required
                      autoComplete="new-password"
                      data-testid="set-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="set-password-toggle"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 pr-12 h-12"
                      required
                      autoComplete="new-password"
                      data-testid="set-password-confirm-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="set-password-confirm-toggle"
                    >
                      {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl text-base btn-press"
                  disabled={submitting}
                  data-testid="set-password-submit"
                >
                  {submitting ? 'Saving…' : (fromCheckout ? 'Claim account' : 'Update password')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
